/**
 * HMAC-signed session tokens for the AuctionRadar API.
 *
 * Format: `<base64url(payload-json)>.<base64url(hmac-sha256)>`
 *
 * Why HMAC and not JWT: we control both endpoints, never need third-party
 * verification, and want zero dependencies. SubtleCrypto.HMAC is built into
 * Workers and the browser. No `jsonwebtoken` install, no JWK rotation.
 *
 * The token is stored in an HttpOnly cookie set by the API, sent back on
 * every request. The dashboard never sees the raw token (HttpOnly hides it
 * from JS) — it relies on `credentials: 'include'` to ride the cookie along.
 */

const enc = new TextEncoder();
const dec = new TextDecoder();

export type SessionRole = 'admin' | 'partner' | 'viewer';

export type SessionPayload = {
  /** User ID — primary key in the `users` table. */
  uid: string;
  role: SessionRole;
  /** Unix seconds. Tokens past this are rejected during verify. */
  exp: number;
};

function b64urlEncode(bytes: Uint8Array): string {
  let bin = '';
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function b64urlDecode(s: string): Uint8Array {
  const padLen = (4 - (s.length % 4)) % 4;
  const padded = s.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat(padLen);
  const bin = atob(padded);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

async function importKey(secret: string): Promise<CryptoKey> {
  return crypto.subtle.importKey(
    'raw',
    enc.encode(secret),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign', 'verify'],
  );
}

/**
 * Sign a session payload with the given HMAC secret.
 * The output is opaque from the client's perspective — they store it in a
 * cookie and send it back; only servers with the secret can verify.
 */
export async function signSession(
  payload: SessionPayload,
  secret: string,
): Promise<string> {
  const body = b64urlEncode(enc.encode(JSON.stringify(payload)));
  const key = await importKey(secret);
  const sigBytes = new Uint8Array(
    await crypto.subtle.sign('HMAC', key, enc.encode(body)),
  );
  return `${body}.${b64urlEncode(sigBytes)}`;
}

/**
 * Verify a session token and return its payload, or null if:
 *   - the format is malformed
 *   - the HMAC doesn't match (tampered or wrong secret)
 *   - `exp` has passed
 *
 * Constant-time HMAC comparison is delegated to `crypto.subtle.verify`.
 */
export async function verifySession(
  token: string,
  secret: string,
): Promise<SessionPayload | null> {
  const parts = token.split('.');
  if (parts.length !== 2) return null;
  const [body, sig] = parts;
  if (!body || !sig) return null;

  const key = await importKey(secret);
  let sigOk: boolean;
  try {
    sigOk = await crypto.subtle.verify(
      'HMAC',
      key,
      b64urlDecode(sig) as BufferSource,
      enc.encode(body),
    );
  } catch {
    return null;
  }
  if (!sigOk) return null;

  let payload: SessionPayload;
  try {
    payload = JSON.parse(dec.decode(b64urlDecode(body))) as SessionPayload;
  } catch {
    return null;
  }
  if (
    typeof payload.uid !== 'string' ||
    typeof payload.role !== 'string' ||
    typeof payload.exp !== 'number'
  ) {
    return null;
  }
  if (payload.exp < Math.floor(Date.now() / 1000)) return null;
  return payload;
}

/**
 * Parse a single cookie value out of a `Cookie` header by name.
 * Returns null if the header is missing or the cookie isn't present.
 *
 * Intentionally minimal — does not handle attributes like `; Path=...` (those
 * only appear in `Set-Cookie`, never in `Cookie`).
 */
export function parseCookie(header: string | null, name: string): string | null {
  if (!header) return null;
  const parts = header.split(';');
  for (const part of parts) {
    const eq = part.indexOf('=');
    if (eq < 0) continue;
    const k = part.slice(0, eq).trim();
    if (k !== name) continue;
    return part.slice(eq + 1).trim();
  }
  return null;
}

export const SESSION_COOKIE_NAME = 'auctionradar_session';
export const SESSION_TTL_SECONDS = 60 * 60 * 24 * 7; // 7 days
